// Global Instructions Rule Applied!
//
// Configuration → Fund Statuses services: list with usage counts, CRUD on
// cfg.fund_statuses, and the reassign-and-replace flow. Same fetch
// conventions as documentConfigService; every route is admin-gated at the
// backend (403 surfaces readably from the body). An in-use delete returns 409
// with usage detail — the panel routes that into the Replace modal.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  FundStatusAdminRow,
  validateFundStatusAdminRow
} from '../../../state_manager/data_models/fund_statuses_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export class StatusInUseError extends Error {
  usageCount: number
  constructor(message: string, usageCount: number) {
    super(message)
    this.name = 'StatusInUseError'
    this.usageCount = usageCount
  }
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchFundStatuses = async (
  accessToken: string | null
): Promise<FundStatusAdminRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.FUND_STATUSES_LIST),
    { method: API_ROUTES.CONFIG.FUND_STATUSES_LIST.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as FundStatusAdminRow[]
  validateFundStatusAdminRow(rows, { isArray: true })
  return rows
}

export const createFundStatus = async (
  fields: {
    name: string
    category: string
    value?: string | null
    sort_order?: number | null
    // Behaviour is chosen ONCE, on create. Flipping is_open on an existing row
    // silently re-scopes every fund picker and the prospect-follower gate
    // app-wide, with no undo — same shape as the Pipeline kind field, which is
    // disabled when editing.
    is_open?: boolean
    is_terminal?: boolean
  },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.FUND_STATUS_CREATE),
    {
      method: API_ROUTES.CONFIG.FUND_STATUS_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateFundStatus = async (
  statusId: string,
  fields: { name?: string; sort_order?: number | null },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.FUND_STATUS_UPDATE, { statusId }),
    {
      method: API_ROUTES.CONFIG.FUND_STATUS_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteFundStatus = async (
  statusId: string,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.FUND_STATUS_DELETE, { statusId }),
    { method: API_ROUTES.CONFIG.FUND_STATUS_DELETE.method, headers: authHeaders(accessToken) }
  )
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}))
    throw new StatusInUseError(
      body.message || body.error || 'This status is in use',
      Number(body.usage_count || 0)
    )
  }
  await readJson(response)
}

export const reassignFundStatuses = async (
  sourceIds: string[],
  targetId: string,
  deleteSources: boolean,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.FUND_STATUSES_REASSIGN),
    {
      method: API_ROUTES.CONFIG.FUND_STATUSES_REASSIGN.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({
        data: { source_ids: sourceIds, target_id: targetId, delete_sources: deleteSources }
      })
    }
  )
  await readJson(response)
}
